import { createStore } from 'vuex';
import { queryTypes, badgeTypes } from 'JIX/components/jobsearch/constants.js';
import { splitQstring } from 'JIX/jobsearch/utils.js';
import { __ } from 'JIX/gettext.js';
import { processParams } from 'JIX/jobsearch/utils.js';

import {
    search,
    searchAnalysis,
    searchMap,
    searchUnsolicited,
    hitcount,
} from 'JIX/jobsearch/api.js';

let controller = new AbortController();

function createValueParameterMap(list) {
    const out = {};
    list.forEach(([parameterName, parameterValue]) => {
        out[parameterValue] = parameterName;
    });
    return out;
}


function selectedValues(query, valueParameterMap) {
    const out = {};
    queryTypes.forEach(paramType => {
        let parameters = query[paramType];
        if (parameters) {
            if (!Array.isArray(parameters)) {
                parameters = [parameters];
            }
            if (!parameters.length) {
                return;
            }
            out[paramType] = parameters.map(value => {
                const text = valueParameterMap[paramType]?.[value];
                return {
                    text: text ?? value,
                    value
                };
            });
        }
    });
    if (query.address && query.radius) {
        const text = query.radius + ' ' + __('km radius');
        const nearlocationBadge = { text, badgeType: badgeTypes.NEARLOCATION };
        if (Array.isArray(out.geoareaid)) {
            out.geoareaid.push(nearlocationBadge);
        } else {
            out.geoareaid = [nearlocationBadge];
        }
    }
    return out;
}


const getters = {
    /** The workingQuery is a combination of the searchQuery
     * (the query that produced the results currently shown to the user)
     * and the searchQueryWorking (the unsubmitted changes to the filters)*/
    workingQuery(state) {
        const out = { ...state.searchQuery, ...state.searchQueryWorking };
        const filterLists = [
            'employment_type',
            'workinghours_type',
            'employment_place',
            'subid',
            'jobtitleid',
            'companyid',
            'geoareaid',
            'flag',
        ];
        filterLists.forEach(list => {
            if (!out[list]) {
                out[list] = [];
            }
        });
        return out;

    },
    selectedValues(state, getters) {
        return (queryType) => {
            let query;
            switch (queryType) {
                case 'searchQueryWorking':
                    query = state.searchQueryWorking;
                    break;
                case 'searchQuery':
                    query = state.searchQuery;
                    break;
                case 'workingQuery':
                    query = getters.workingQuery;
                    break;
                default:
                    query = getters.workingQuery;
            }
            return selectedValues(query, getters.valueParameterMap);
        };
    },
    requestInProgress(state) {
        return state.pendingRequests > 0;
    },
    valueParameterMap(state) {
        return {
            ...state.dynamicValueParameterMap,
            employment_type: createValueParameterMap(state.employmentTypes ?? []),
            workinghours_type: createValueParameterMap(state.workinghoursTypes ?? []),
            employment_place: createValueParameterMap(state.employmentPlaces ?? []),
            jobage: createValueParameterMap(state.jobageOptions ?? []),
            subid: Object.fromEntries((state.subjobcategory_list ?? []).flatMap(([_, sup]) => sup.map(sub => [sub[1], sub[0]]))),
        };
    },
    isAdmin(state) {
        return !!state.extraAPIParams?.admin;
    },
    isArchive(state) {
        return state.enableArchive && (state.searchQuery.jobage ?? '') === 'archive';
    },
    isCombinedArchive(state) {
        return state.enableArchive && (state.searchQuery.archive ?? 0) === 2;
    },
    hasNearLocation(_state, getters) {
        return getters.workingQuery.address !== '' && getters.workingQuery.radius > 0;
    }
};

const mutations = {
    startRequest(state) {
        state.pendingRequests++;
    },
    endRequest(state) {
        if (state.pendingRequests === 0) {
            console.error("No pending requests when ending");
        } else {
            state.pendingRequests--;
        }
    },
    setParam(state, { key, value }) {
        if (Array.isArray(value)) {
            const uniques = [];
            value.forEach(element => {
                if (!uniques.includes(element)) {
                    uniques.push(element);
                }
            });
            state.searchQueryWorking[key] = uniques;
        } else {
            state.searchQueryWorking[key] = value;
        }

        if (key === 'jobage' && value !== 'jobage') {
            state.searchQueryWorking['archive'] = null;
            state.searchQueryWorking['mindate'] = null;
            state.searchQueryWorking['maxdate'] = null;
        }
    },
    // Use the action addParam instead of the mutation to ensure the same parameter is not added several times
    addParam(state, { key, value }) {
        if (state.searchQueryWorking[key]) {
            if (!Array.isArray(state.searchQueryWorking[key])) {
                state.searchQueryWorking[key] = [state.searchQueryWorking[key]];
            } else {
                state.searchQueryWorking[key].push(value);
            }
        } else {
            let initialParams = state.searchQuery?.[key] ?? [];
            state.searchQueryWorking[key] = [...(Array.isArray(initialParams) ? initialParams : [initialParams]), value];
        }
    },
    clearCategoryParams(state) {
        state.searchQueryWorking.subid = [];
        state.searchQueryWorking.supid = [];
    },
    clearGeoParams(state) {
        state.searchQueryWorking.radius = null;
        state.searchQueryWorking.latitude = null;
        state.searchQueryWorking.longitude = null;
        state.searchQueryWorking.address = '';
        state.searchQueryWorking.jobage = '';
        state.searchQueryWorking.geoareaid = [];
    },
    clearNearLocation(state) {
        state.searchQueryWorking.radius = null;
        state.searchQueryWorking.address = '';
    },
    clearParams(state) {
        queryTypes.forEach((queryParam) => {
            state.searchQueryWorking[queryParam] = [];
        });
        //Clear super category separately from querytypes,
        //as adding supid in querytypes would show its ID in
        //the search field
        state.searchQueryWorking.supid = [];
        state.searchQueryWorking.radius = null;
        state.searchQueryWorking.latitude = null;
        state.searchQueryWorking.longitude = null;
        state.searchQueryWorking.address = '';
        state.searchQueryWorking.jobage = '';
        state.searchQueryWorking.mindate = '';
        state.searchQueryWorking.maxdate = '';
        if (getters.isAdmin(state)) {
            state.searchQueryWorking.flag = [];
        }
    },
    clearSearchQueryWorking(state) {
        state.searchQueryWorking = {};
    },
    clearJobageValues(state) {
        state.searchQueryWorking.mindate = '';
        state.searchQueryWorking.maxdate = '';
    },
    processGeoParams(state) {
        if (state.searchQueryWorking.address && !state.searchQueryWorking.radius){
            state.searchQueryWorking.address = '';
        } else if (!state.searchQueryWorking.address && state.searchQueryWorking.radius){
            state.searchQueryWorking.radius = null;
        }
    },
    setDefaultJobageValues(state) {
        state.searchQueryWorking.mindate = state.archiveMindate ?? '';
        state.searchQueryWorking.maxdate = state.archiveMaxdate ?? '';
    },
    setSearchQuery(state, value) {
        state.searchQuery = value;
        state.searchQueryWorking = {};
    },
    setSearchResponse(state, response) {
        state.searchResponse = response;
    },
    setWorkingQueryHitcount(state, value) {
        state.workingQueryHitcount = value;
    },
    setApiValidationErrors(state, errors) {
        state.apiValidationErrors = errors;
    },
    updateDynamicValueParameterMap(state, { type, text, value }) {
        state.dynamicValueParameterMap[type] ??= {};
        state.dynamicValueParameterMap[type][value] = text;
    },
    updateState(state, { stateKey, value }) {
        state[stateKey] = value;
    },
    setAdmin(state, value) {
        state.extraAPIParams.admin = value ? 1 : 0;
    },
    updateViewMode(state, value) {
        state.viewMode = value;
    },
    updateIsFollowingCompany(state, { following, index }) {
        state.searchResponse.companies[index].isFollowing = following;
    },
};

async function doRequest(commit, f, ...args) {
    commit('startRequest');
    try {
        const options = {
            signal: controller.signal,
        };
        const res = await f(...args, options);
        if (res.ok) {
            return await res.json();
        }
    } finally {
        commit('endRequest');
    }
}

function getSearchFunction(viewMode) {
    switch (viewMode) {
        case "analysis":
            return searchAnalysis;
        case "list":
            return search;
        case "map":
            return searchMap;
        case "unsolicited":
            return searchUnsolicited;
        default:
            throw new Error("Unknown view mode");
    }
}

const actions = {
    async performSearch({ commit, state, getters }, { newViewMode } = {}) {
        if (newViewMode) {
            delete state.searchQuery.sort;
        }
        const viewMode = newViewMode || state.viewMode;
        const params = {
            ...getters.workingQuery,
            ...state.extraAPIParams,
        };
        const processedParams = processParams(params);
        if (!processedParams.sort && viewMode === 'list'){
            processedParams.sort = 'score';
        }
        if (newViewMode) {
            processedParams.page = 1;
        }
        if (state.pendingRequests) {
            controller.abort();
            controller = new AbortController();
        }
        const res = await doRequest(commit, getSearchFunction(viewMode), processedParams);
        if (res) {
            const newSearchQuery = { ...getters.workingQuery };
            if (newViewMode) {
                if (newViewMode === 'list') {
                    newSearchQuery.sort = 'score';
                } else if (newViewMode === 'unsolicited') {
                    newSearchQuery.sort = 'default';
                }
                newSearchQuery.page = 1;
            }
            commit('setSearchQuery', newSearchQuery);
            commit('setSearchResponse', res);
            if (newViewMode){
                commit('updateViewMode', newViewMode);
            }
        }
    },
    async fetchHitcount({ commit, getters }) {
        const params = {
            ...getters.workingQuery,
        };
        const processedParams = processParams(params);
        let apiValidationErrors = [];
        const response = await hitcount(processedParams);
        if (response.ok) {
            const result = await response.json();
            commit('setWorkingQueryHitcount', result.hitcount);
        } else if (response.status === 422) {
            const result = await response.json();
            apiValidationErrors = result.errors;
        } else {
            throw Error(`fetchHitcount failed [${response?.status ?? 'No status code'}]`);
        }
        commit('setApiValidationErrors', apiValidationErrors);
    },
    async goToPage({ commit, dispatch }, page) {
        commit('setParam', { key: 'page', value: page });
        return dispatch('performSearch');
    },
    addParam(context, { key, value }) {
        const currentParaeterArray = context.getters.workingQuery[key];
        if (Array.isArray(currentParaeterArray) && currentParaeterArray.includes(value)) {
            return;
        }
        context.commit('addParam', { key, value });
    },
};

export default function (data) {
    const store = createStore({
        state: () => {
            const state = {
                searchQueryWorking: {},
                searchQuery: {},
                pendingRequests: 0,
                dynamicValueParameterMap: {},
                showDropdown: false,
                showDropdownGeo: false,
                userInput: '',
                userInputGeo: '',
                ...data,
            };
            if (state.searchQuery?.q) {
                state.searchQuery.q = splitQstring(state.searchQuery.q);
            }
            state.workingQueryHitcount = state.searchResponse?.hitcount ?? null;
            return state;
        },
        getters,
        mutations,
        actions,
    });

    return store;
}
